import { PostHogProvider, usePostHog } from "@posthog/react";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  redirect,
  Scripts,
  useRouterState,
} from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";

import {
  aiReferrerOf,
  analyticsEnabled,
  POSTHOG_TOKEN,
  posthogOptions,
} from "../lib/analytics";
import { documentHeaders, documentLang } from "../lib/seo";
import { collectVitals } from "../lib/vitals";
import appCss from "../styles.css?url";

interface MyRouterContext {
  queryClient: QueryClient;
}

const TITLE = "Bible Shield";
const DESCRIPTION =
  "Pray to unlock your apps. Bible Shield turns the reach for your phone into a moment of prayer.";

export const Route = createRootRouteWithContext<MyRouterContext>()({
  /**
   * The one thing that has to happen before any route matches: get the visitor
   * onto the canonical hostname.
   *
   * Server-only, and the dynamic import is what keeps it that way — the module
   * it pulls in reads the incoming request, which does not exist in a browser,
   * and a static import would drag it into the client graph. `beforeLoad` runs
   * on both sides, so the guard is not optional.
   */
  beforeLoad: async ({ location }) => {
    if (!import.meta.env.SSR) return;
    const { canonicalHostRedirect } = await import("../lib/canonicalHost");
    const href = canonicalHostRedirect(location.pathname, location.searchStr);
    if (href)
      throw redirect({
        href,
        statusCode: 301,
        // NOT cacheable, and this is load-bearing rather than cautious.
        // Workers Caching keys on the path and not the host (see the `cache`
        // block in wrangler.jsonc), so a cached `www` redirect for `/` would be
        // handed to apex visitors as a redirect to the page they are already
        // on — an infinite bounce, served from cache, for everyone.
        headers: { "cache-control": "no-store" },
      });
  },
  // Cache-Control and HSTS for the pages that reach here without a route of
  // their own — chiefly the 404 below. Every real route sets its own.
  headers: documentHeaders,
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      // Shield Cobalt — the browser chrome tint on Android and in a phone's
      // task switcher. It was #000000 from the boilerplate, which is the one
      // colour the palette explicitly does not contain.
      { name: "theme-color", content: "#2952E3" },
      // Light only (docs/DESIGN-SYSTEM.md §9). Without this a dark-mode browser
      // renders form controls, scrollbars and its own UA styles dark around a
      // page that is deliberately cream.
      { name: "color-scheme", content: "light" },
      // Bing Webmaster Tools ownership. Not a secret and not removable once
      // verification is done — Bing re-checks it, and a site that stops
      // asserting ownership silently loses the property along with its data.
      //
      // Here on the ROOT rather than only on the home page, which is what Bing
      // asks for, because every page of this site is server-rendered by the
      // same shell and one place is cheaper to keep true than nine.
      //
      // Google's equivalent is a DNS TXT record on the zone, not a tag — the
      // two verifications live in different places on purpose.
      { name: "msvalidate.01", content: "F4A164AACC9E88923171A9E651FB36EF" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // The site had no icon at all, which a browser answers by requesting
      // /favicon.ico, getting the app shell's HTML back with a 200, and showing
      // the blank-page glyph — on the tab, on a bookmark, and in the Google
      // result next to the title. All three files are the app icon resized by
      // `scripts/og-image.py` and committed.
      { rel: "icon", href: "/favicon.ico", sizes: "48x48" },
      {
        rel: "icon",
        href: "/icon-192.png",
        type: "image/png",
        sizes: "192x192",
      },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      // The two faces above the fold. Preloaded because `font-display: swap`
      // means the alternative is a visible reflow from the fallback stack on
      // the first paint of every page.
      {
        rel: "preload",
        href: "/fonts/baloo2-700.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        href: "/fonts/nunito-400.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      // Nunito Bold was the tail of the critical request chain Lighthouse
      // flagged: the CSS asks for it, so the browser only discovers it after
      // the stylesheet has parsed — 195ms into the load, for type that is on
      // screen immediately (the store buttons, every card heading). Preloading
      // it moves the request to head-parse time alongside the other two.
      {
        rel: "preload",
        href: "/fonts/nunito-700.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
    ],
  }),
  shellComponent: RootDocument,
  errorComponent: RootError,
  notFoundComponent: RootNotFound,
});

/**
 * The 404.
 *
 * Reached by a mistyped language segment (`/zz`), a stale share link, and
 * whatever else the internet points at a domain. Without it the router renders
 * its own bare "Not Found" — on our domain, in our type-less default font, as
 * somebody's first impression.
 *
 * English only, and that is the honest choice rather than a shortcut: a 404 has
 * no language of its own to be in. The visitor is one link from a page that is
 * in all nine.
 */
function RootNotFound() {
  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-xl flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        404
      </p>
      <h1 className="font-display text-[clamp(1.75rem,6vw,2.5rem)] leading-tight font-bold tracking-tight text-ink">
        This page does not exist
      </h1>
      <p className="leading-relaxed text-body">
        The link may be old, or the address may have a typo in it.
      </p>
      <a
        href="/"
        className="rim mt-2 inline-flex h-[54px] items-center justify-center bg-brand px-7 font-display text-[17px] font-bold text-onbrand"
      >
        go to the home page
      </a>
    </main>
  );
}

/**
 * What a visitor sees when a route throws.
 *
 * Without it TanStack Router's default takes over, which on a production build
 * is a bare unstyled message — served under our own domain, to someone who
 * arrived from a link a friend sent them, as their first impression of the
 * product. The page is server-rendered, so the throw itself is already recorded
 * in Workers Logs (see the `observability` block in wrangler.jsonc); this is
 * only the half the visitor sees.
 *
 * Kept to plain elements and inline styles on purpose: a stylesheet that failed
 * to load is one of the things that can put a visitor here.
 */
function RootError({ error }: { error: Error }) {
  // Server-side this reaches Workers Logs; in a browser it reaches the console.
  // Either way it is the only line that names WHICH route died.
  console.error({
    level: "error",
    event: "web.route_error",
    error: error.message,
  });
  return (
    <main
      style={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.75rem",
        padding: "2rem",
        textAlign: "center",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <h1 style={{ fontSize: "1.5rem", fontWeight: 700 }}>
        Something went wrong
      </h1>
      <p style={{ color: "#6E7180", maxWidth: "36rem" }}>
        This page could not be loaded. Try again, or open Bible Shield on your
        phone.
      </p>
      <a href="/" style={{ color: "#2952E3", textDecoration: "underline" }}>
        Go to the home page
      </a>
    </main>
  );
}

/**
 * The document shell, with analytics wrapped around it.
 *
 * `PostHogProvider` is SAFE TO RENDER ON THE SERVER even though posthog-js is a
 * browser library: it only calls `init` from a `useEffect`, which the Worker
 * never runs. That is what lets it sit here in the shell rather than behind a
 * mounted-yet guard, so every route is inside it from the first client render
 * and no pageview is lost to hydration timing.
 *
 * NOT MOUNTED AT ALL when analytics is off, rather than mounted-and-silent. The
 * `analyticsEnabled` guard inside `useTrack` only covers events WE send; the
 * SDK's own automatic pageview capture answers to `init`, not to us. Mounting
 * the provider on a dev server would therefore have filed every localhost
 * navigation and every hot reload into the same project the install funnel is
 * read from — which, on a site with this little traffic, would be most of the
 * data in it.
 */
/**
 * Starts Core Web Vitals collection once the page is in a browser.
 *
 * A component rather than a call inside `RootDocument`, so it can use the
 * PostHog client from context and so React owns the teardown. It renders
 * nothing and it is inside the provider on purpose — outside it, `usePostHog`
 * returns undefined and the metrics go nowhere.
 */
function Vitals() {
  const posthog = usePostHog();
  useEffect(() => {
    if (!posthog) return;

    // Tag the session with the assistant that sent this visitor, if one did.
    // A super property rather than an event, so every later event carries it —
    // see `aiReferrerOf` for why that is the question worth being able to ask.
    const ai = aiReferrerOf(document.referrer);
    if (ai) posthog.register({ ai_referrer: ai });

    return collectVitals(posthog);
  }, [posthog]);
  return null;
}

function RootDocument({ children }: { children: ReactNode }) {
  // Read off the location rather than off a route's params, because the two URL
  // shapes that carry a language belong to different routes (`/ko` vs
  // `/privacy?lang=ko`) and the shell sits above both. `documentLang` is the one
  // place that mapping lives; `seo.test.ts` covers it.
  const location = useRouterState({ select: (state) => state.location });
  const lang = documentLang(location.pathname, location.searchStr);

  return (
    <html lang={lang}>
      <head>
        <HeadContent />
      </head>
      <body>
        {analyticsEnabled ? (
          <PostHogProvider apiKey={POSTHOG_TOKEN} options={posthogOptions}>
            <Vitals />
            {children}
          </PostHogProvider>
        ) : (
          children
        )}
        <Scripts />
      </body>
    </html>
  );
}
